import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Typography,
  message,
} from "antd";
import { BarChartOutlined, CodeOutlined, CopyOutlined, EditOutlined, LinkOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { CHAVE_SPEC_IMPORTADO } from "../Graficos/GeradorGraficos";
import { Content } from "antd/es/layout/layout";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { token } from "../../functions/request";
import { buildMiniChartOptions, generateEmbedHtml, specUsdaParaGerador } from "./usda/miniGrafico";
import { MESES_LONGOS, montarGrafico, naoAcumula } from "./usdaNass/montarGraficos";
import PainelEstados from "./usdaNass/PainelEstados";

const { Title, Text } = Typography;
const FONTE = "RURAL BUSINESS/USDA-NASS";

/**
 * Gráficos dos relatórios NASS (Crop Production, Cattle on Feed, Milk
 * Production, Livestock Slaughter), no mesmo formato do "Gráfico USDA".
 *
 * Os dados vêm da Quick Stats via API do painel (/nass). O minigráfico e o
 * código de incorporação são os mesmos do USDA.js, então o resultado nas
 * matérias é idêntico ao que a redação já usa.
 */

/** fetch com status e corpo de erro — o `request` do sistema descarta os dois. */
async function chamarApi(caminho, opcoes = {}) {
  const resposta = await fetch(process.env.REACT_APP_API_RB + caminho, {
    headers: { "Content-Type": "application/json", "x-access-token": token },
    ...opcoes,
  });
  const corpo = await resposta.json().catch(() => ({}));
  if (!resposta.ok) {
    const erro = new Error(corpo.error || resposta.statusText);
    erro.code = corpo.code;
    throw erro;
  }
  return corpo;
}

export default function USDANass() {
  const [catalogo, setCatalogo] = useState([]);
  const [carregandoCatalogo, setCarregandoCatalogo] = useState(true);
  const [relatorioId, setRelatorioId] = useState("crop-production");
  const [seriesSelecionadas, setSeriesSelecionadas] = useState([]);
  const [anos, setAnos] = useState(6);
  const [modo, setModo] = useState("mesmoMes");
  const [mes, setMes] = useState(null);
  const [cor, setCor] = useState("#39FF14");
  const [visao, setVisao] = useState("historico");
  const [metrico, setMetrico] = useState(true);
  const ehPopup = useMemo(() => new URLSearchParams(window.location.search).get("popup") === "1", []);
  const navigate = useNavigate();

  const [dados, setDados] = useState({});
  const [anosBuscados, setAnosBuscados] = useState(0);
  const [buscando, setBuscando] = useState(false);
  const [erro, setErro] = useState(null);
  const [usouCache, setUsouCache] = useState(false);

  const [embedHtml, setEmbedHtml] = useState("");
  const [embedAberto, setEmbedAberto] = useState(false);
  const [copiado, setCopiado] = useState(null);
  const dimensoes = useRef({ width: 500, height: 300 });

  useEffect(() => {
    chamarApi("/nass/catalogo")
      .then((r) => setCatalogo(r.relatorios || []))
      .catch((e) => setErro({ mensagem: `Não foi possível carregar os relatórios: ${e.message}` }))
      .finally(() => setCarregandoCatalogo(false));
  }, []);

  const relatorio = catalogo.find((r) => r.id === relatorioId);
  const seriesDoRelatorio = useMemo(() => relatorio?.series || [], [relatorio]);
  const ehAnual = seriesDoRelatorio.every((s) => s.tipo === "anual");
  const algumaNaoAcumula = seriesDoRelatorio
    .filter((s) => seriesSelecionadas.includes(s.id))
    .some(naoAcumula);

  const trocarRelatorio = (id) => {
    setRelatorioId(id);
    setSeriesSelecionadas([]);
    setDados({});
    setAnosBuscados(0);
    setMes(null);
    setErro(null);
  };

  const buscar = async () => {
    if (!seriesSelecionadas.length) {
      message.warning("Selecione ao menos uma série.");
      return;
    }
    setBuscando(true);
    setErro(null);
    try {
      // Um ano a mais que o exibido: o acumulado e a variação de 1 ano do
      // primeiro ponto precisam do ano anterior.
      const resposta = await chamarApi("/nass/series", {
        method: "POST",
        body: JSON.stringify({ series: seriesSelecionadas, anos: anos + 1 }),
      });
      setDados((anteriores) => ({
        ...anteriores,
        ...Object.fromEntries((resposta.series || []).map((s) => [s.id, s])),
      }));
      setAnosBuscados(anos);
      setUsouCache(Boolean(resposta.fromCache));
    } catch (e) {
      setErro({ mensagem: e.message, semChave: e.code === "NASS_SEM_CHAVE" });
    } finally {
      setBuscando(false);
    }
  };

  const graficos = useMemo(() => {
    return seriesSelecionadas
      .map((id) => seriesDoRelatorio.find((s) => s.id === id))
      .filter(Boolean)
      .filter((serie) => dados[serie.id])
      .map((serie) => {
        const montado = montarGrafico(serie, dados[serie.id], { anos, modo, mes, metrico });
        if (montado.vazio) return { key: serie.id, serie, vazio: montado.motivo };
        const formatarValor = (() => {
          const porValor = new Map(montado.pontos.map((p) => [p.valorFinal, p.rotulo]));
          return (v) => porValor.get(v) ?? "";
        })();
        return {
          key: `${serie.id}-${montado.modo}-${montado.mes}`,
          serie,
          montado,
          options: buildMiniChartOptions(
            montado.pontos,
            serie.mercado,
            serie.atributo,
            "Estados Unidos",
            montado.unidadeTexto,
            cor,
            { fonte: FONTE, periodoTitulo: "", linhaExtra: montado.linhaExtra, formatarValor }
          ),
          embedData: {
            nomeMercado: serie.mercado,
            atributo: serie.atributo,
            linhaExtra: montado.linhaExtra,
            local: "Estados Unidos",
            unit: montado.unidadeTexto,
            unidadeTexto: montado.unidadeTexto,
            fonte: FONTE,
            color: cor,
            sortedPoints: montado.pontos.map((p) => ({
              safra: p.safra,
              anoNum: p.anoNum,
              valor: p.valorFinal,
              rotulo: p.rotulo,
            })),
          },
        };
      });
  }, [seriesSelecionadas, seriesDoRelatorio, dados, anos, modo, mes, cor, metrico]);

  // Edição exibida: data em que o USDA carregou a série (load_time, horário
  // de Washington) e o último mês publicado. O relatório sai com os dados do
  // mês anterior — o de 22/out traz setembro —, então sem esta linha o
  // "set" do gráfico parecia ser a edição antiga.
  const edicao = useMemo(() => {
    const series = seriesSelecionadas.map((id) => dados[id]).filter((s) => s?.pontos?.length);
    if (!series.length) return null;
    const publicadoEm = series.map((s) => s.atualizadoEm).filter(Boolean).sort().pop();
    const ultimo = series
      .map((s) => s.pontos[s.pontos.length - 1])
      .sort((a, b) => b.ano - a.ano || (b.mes || 0) - (a.mes || 0))[0];
    const [data] = String(publicadoEm || "").split(" ");
    const [a, m, d] = data ? data.split("-") : [];
    return {
      publicado: d ? `${d}/${m}/${a}` : null,
      ate: ultimo.mes ? `${MESES_LONGOS[ultimo.mes - 1].toLowerCase()} de ${ultimo.ano}` : String(ultimo.ano),
    };
  }, [seriesSelecionadas, dados]);

  const prontos = graficos.filter((g) => g.options);
  const faltaBuscar =
    seriesSelecionadas.some((id) => !dados[id]) || (anosBuscados > 0 && anos > anosBuscados);

  // Um gráfico para o Gerador de imagens. Os pontos já vêm na escala do
  // gráfico (montarGrafico), então a medida só leva unidade e decimais.
  const abrirNoGerador = (grafico) => {
    const { montado, embedData, serie } = grafico;
    const medida = { divisor: 1, unidade: montado.unidadeTexto, decimais: montado.decimais ?? 2, sufixo: "" };
    const subtitulo = [serie.atributo, montado.linhaExtra].filter(Boolean).join(" - ");
    try {
      sessionStorage.setItem(
        CHAVE_SPEC_IMPORTADO,
        JSON.stringify(specUsdaParaGerador({ ...embedData, local: "EUA" }, medida, subtitulo))
      );
    } catch {}
    navigate(`/graficos/gerador${ehPopup ? "?popup=1" : ""}`);
  };

  const copiarUm = (grafico) => {
    const { width, height } = dimensoes.current;
    navigator.clipboard
      .writeText(generateEmbedHtml([grafico], width, height))
      .then(() => {
        setCopiado(grafico.key);
        setTimeout(() => setCopiado(null), 2000);
      })
      .catch(() => message.error("Erro ao copiar."));
  };

  return (
    <Content style={{ margin: "12px 16px" }}>
      <Title level={3}>USDA NASS — Relatórios de produção</Title>

      <Card size="small" style={{ marginBottom: 16 }} loading={carregandoCatalogo}>
        <Space wrap size={[24, 12]} align="center">
          <Space>
            <Text strong>Relatório</Text>
            <Select
              style={{ minWidth: 280 }}
              value={relatorioId}
              onChange={trocarRelatorio}
              options={catalogo.map((r) => ({ value: r.id, label: `${r.nome} (${r.nomePt})` }))}
            />
            {relatorio?.cornell && (
              <a href={relatorio.cornell} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>
                <LinkOutlined /> Edições (Cornell)
              </a>
            )}
          </Space>
          <Space>
            <Text strong>Visão</Text>
            <Radio.Group value={visao} onChange={(e) => setVisao(e.target.value)} optionType="button" buttonStyle="solid" size="small">
              <Radio.Button value="historico">Histórico dos EUA</Radio.Button>
              <Radio.Button value="estado">Ranking por estado</Radio.Button>
            </Radio.Group>
          </Space>
          <Space>
            <Text strong>Unidades</Text>
            <Radio.Group value={metrico} onChange={(e) => setMetrico(e.target.value)} optionType="button" buttonStyle="solid" size="small">
              <Radio.Button value={true}>Métricas (t, ha)</Radio.Button>
              <Radio.Button value={false}>Americanas (bu, acre, lb)</Radio.Button>
            </Radio.Group>
          </Space>
        </Space>
      </Card>

      {visao === "estado" ? (
        <PainelEstados key={relatorioId} relatorio={relatorio} metrico={metrico} chamarApi={chamarApi} ehPopup={ehPopup} />
      ) : (
      <>

      {erro?.semChave && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="A chave da Quick Stats não está configurada"
          description={
            <>
              Os dados vêm da API Quick Stats do USDA/NASS, que exige uma chave gratuita. Cadastre
              um e-mail em{" "}
              <a href="https://quickstats.nass.usda.gov/api" target="_blank" rel="noreferrer">
                quickstats.nass.usda.gov/api
              </a>{" "}
              (a chave chega na hora) e coloque em <code>NASS_API_KEY</code> no <code>.env</code> da
              API, reiniciando o processo em seguida.
            </>
          }
        />
      )}
      {erro && !erro.semChave && (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={erro.mensagem} />
      )}
      {edicao && !erro && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            edicao.publicado
              ? `Edição do USDA de ${edicao.publicado} — dados até ${edicao.ate}.`
              : `Dados até ${edicao.ate}.`
          }
          description='O relatório sai com os números do mês anterior (o de outubro traz setembro). Se acabou de sair uma edição, clique em "Atualizar dados" para trazê-la.'
        />
      )}
      {usouCache && !erro && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="A Quick Stats está indisponível — exibindo os dados da última consulta salva."
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={6} xl={5}>
          <Card title="Histórico dos EUA" size="small">
            <Form layout="vertical" size="small">
              <Form.Item label="Séries">
                <Select
                  mode="multiple"
                  value={seriesSelecionadas}
                  onChange={setSeriesSelecionadas}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Selecione as séries"
                  maxTagCount={3}
                  options={Object.entries(
                    seriesDoRelatorio.reduce((grupos, s) => {
                      (grupos[s.mercado] = grupos[s.mercado] || []).push(s);
                      return grupos;
                    }, {})
                  ).map(([mercado, lista]) => ({
                    label: mercado,
                    options: lista.map((s) => ({ value: s.id, label: `${s.mercado} - ${s.atributo}` })),
                  }))}
                />
              </Form.Item>

              <Form.Item label="Anos exibidos">
                <InputNumber min={2} max={20} value={anos} onChange={(v) => setAnos(v || 6)} style={{ width: "100%" }} />
              </Form.Item>

              {!ehAnual && (
                <>
                  <Form.Item label="Comparação">
                    <Radio.Group
                      value={modo}
                      onChange={(e) => setModo(e.target.value)}
                      optionType="button"
                      buttonStyle="solid"
                      size="small"
                    >
                      <Radio.Button value="mesmoMes">Mesmo mês</Radio.Button>
                      <Radio.Button value="acumulado">Acumulado no ano</Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                  {modo === "acumulado" && algumaNaoAcumula && (
                    <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: -8, marginBottom: 8 }}>
                      Estoque, peso e leite por vaca não têm acumulado oficial e continuam em "mesmo mês".
                    </Text>
                  )}
                  <Form.Item label="Mês de referência">
                    <Select
                      value={mes}
                      onChange={setMes}
                      options={[
                        { value: null, label: "Último disponível" },
                        ...MESES_LONGOS.map((nome, i) => ({ value: i + 1, label: nome })),
                      ]}
                    />
                  </Form.Item>
                </>
              )}

              <Form.Item label="Cor do gráfico">
                <Space>
                  <input
                    type="color"
                    value={cor}
                    onChange={(e) => setCor(e.target.value)}
                    style={{ width: 40, height: 28, padding: 2, cursor: "pointer", borderRadius: 4, border: "1px solid #d9d9d9" }}
                  />
                  <span style={{ fontSize: 12, fontFamily: "monospace" }}>{cor.toUpperCase()}</span>
                  <Button size="small" onClick={() => setCor("#39FF14")}>
                    Reset
                  </Button>
                </Space>
              </Form.Item>

              <Button
                type="primary"
                block
                icon={<BarChartOutlined />}
                loading={buscando}
                onClick={buscar}
                disabled={!seriesSelecionadas.length}
              >
                {faltaBuscar || !anosBuscados ? "Buscar dados" : "Atualizar dados"}
              </Button>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={18} xl={19}>
          {buscando ? (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
              <Spin size="large" />
            </div>
          ) : prontos.length ? (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 8, flexWrap: "wrap" }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Valores dos Estados Unidos. O ano corrente das safras é a previsão mais recente do USDA.
                </Text>
                <Button
                  icon={<CodeOutlined />}
                  onClick={() => {
                    const { width, height } = dimensoes.current;
                    setEmbedHtml(generateEmbedHtml(prontos, width, height));
                    setEmbedAberto(true);
                  }}
                >
                  Gerar código para incorporar
                </Button>
              </div>
              {faltaBuscar && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message='Há séries ou anos novos na seleção — clique em "Buscar dados" para incluí-los.'
                />
              )}
              <Row gutter={[12, 12]}>
                {graficos.map((grafico, idx) =>
                  grafico.options ? (
                    <Col key={grafico.key} xs={24} lg={12}>
                      <div>
                        {/* Botões numa faixa acima do gráfico: por cima dele
                            eles cobriam o título. */}
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginBottom: 4 }}>
                          <Button size="small" icon={<EditOutlined />} onClick={() => abrirNoGerador(grafico)} style={{ fontSize: 11 }}>
                            Gerador
                          </Button>
                          <Button
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => copiarUm(grafico)}
                            style={{
                              fontSize: 11,
                              background: copiado === grafico.key ? "#27ae60" : undefined,
                              borderColor: copiado === grafico.key ? "#27ae60" : undefined,
                              color: copiado === grafico.key ? "#fff" : undefined,
                            }}
                          >
                            {copiado === grafico.key ? "Copiado!" : "Copiar"}
                          </Button>
                        </div>
                        <HighchartsReact
                          highcharts={Highcharts}
                          options={grafico.options}
                          callback={
                            idx === 0
                              ? (chart) => {
                                  // Código incorporado em 500px, como sempre: a
                                  // largura da tela agora acompanha a coluna.
                                  dimensoes.current = { width: 500, height: chart.chartHeight };
                                }
                              : undefined
                          }
                        />
                      </div>
                    </Col>
                  ) : (
                    <Col key={grafico.key} xs={24} lg={12}>
                      <Card size="small">
                        <Text type="secondary">
                          {grafico.serie.mercado} - {grafico.serie.atributo}:{" "}
                          {grafico.vazio === "sem-chave"
                            ? "sem dados (chave da Quick Stats não configurada)."
                            : grafico.vazio === "indisponivel"
                            ? "a Quick Stats não respondeu e não há cópia salva."
                            : "sem dados para o período ou mês escolhido."}
                        </Text>
                      </Card>
                    </Col>
                  )
                )}
              </Row>
            </>
          ) : (
            <Card size="small">
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
                <Title level={4} type="secondary" style={{ textAlign: "center" }}>
                  {graficos.length
                    ? "Nenhum dado para o período ou mês escolhido."
                    : 'Escolha o relatório e as séries e clique em "Buscar dados"'}
                </Title>
              </div>
            </Card>
          )}
        </Col>
      </Row>
      </>
      )}

      <Modal
        title="Código HTML para incorporar"
        open={embedAberto}
        onCancel={() => setEmbedAberto(false)}
        width={800}
        footer={
          <Button
            type="primary"
            icon={<CopyOutlined />}
            onClick={() =>
              navigator.clipboard
                .writeText(embedHtml)
                .then(() => message.success("Código copiado para a área de transferência!"))
                .catch(() => message.error("Erro ao copiar. Selecione manualmente."))
            }
          >
            Copiar código
          </Button>
        }
      >
        <Input.TextArea value={embedHtml} autoSize={{ minRows: 8, maxRows: 20 }} readOnly style={{ fontFamily: "monospace", fontSize: 11 }} />
      </Modal>
    </Content>
  );
}
